import Alpine from "alpinejs";
import Swiper from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";

if (typeof document !== "undefined") {
  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");
}

// Components
const initLogoStripSwipers = () => {
  document
    .querySelectorAll(
      '[data-swiper="logo-strip"]:not([data-swiper-initialized])',
    )
    .forEach((element) => {
      element.dataset.swiperInitialized = "true";

      new Swiper(element, {
        modules: [Autoplay],
        loop: element.querySelectorAll(".swiper-slide").length > 4,
        speed: 600,
        spaceBetween: 20,
        slidesPerView: 1.35,
        watchOverflow: true,
        autoplay: {
          delay: 3200,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        breakpoints: {
          640: {
            slidesPerView: 2.4,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 24,
          },
          1280: {
            slidesPerView: 5,
            spaceBetween: 24,
          },
        },
      });
    });
};

const initRecentProjectSwipers = () => {
  document
    .querySelectorAll(
      '[data-swiper="recent-projects"]:not([data-swiper-initialized])',
    )
    .forEach((element) => {
      element.dataset.swiperInitialized = "true";

      const id = element.dataset.swiperId;

      new Swiper(element, {
        modules: [Navigation],
        loop: element.querySelectorAll(".swiper-slide").length > 3,
        speed: 600,
        spaceBetween: 20,
        slidesPerView: 1.08,
        watchOverflow: true,
        navigation: {
          prevEl: document.querySelector(`[data-swiper-prev="${id}"]`),
          nextEl: document.querySelector(`[data-swiper-next="${id}"]`),
        },
        breakpoints: {
          640: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        },
      });
    });
};

Alpine.data("projectGallerySwiper", () => ({
  swiper: null,

  init() {
    this.swiper = new Swiper(this.$refs.swiper, {
      modules: [Navigation],
      loop: this.$refs.swiper.querySelectorAll(".swiper-slide").length > 3,
      speed: 600,
      spaceBetween: 20,
      slidesPerView: 1.15,
      watchOverflow: true,
      navigation: {
        prevEl: this.$refs.prev,
        nextEl: this.$refs.next,
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 24,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 32,
        },
      },
    });
  },
}));

// Global
window.Alpine = Alpine;

// Inits
initLogoStripSwipers();
initRecentProjectSwipers();

document.addEventListener("DOMContentLoaded", initLogoStripSwipers);
document.addEventListener("DOMContentLoaded", initRecentProjectSwipers);
document.addEventListener("DOMContentLoaded", initFadeContent);

Alpine.start();

// Fade in block content as sections scroll into view
function initFadeContent() {
  const selector = "section.section-frame > .container-site > *";

  const fadeBlocks = Array.from(document.querySelectorAll(selector));
  if (!fadeBlocks.length) return;

  fadeBlocks.forEach((block) => {
    Array.from(block.children).forEach((child, index) => {
      child.style.setProperty("--fade-index", String(index));
    });
  });

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        target.classList.add("visible");
        obs.unobserve(target);
      });
    },
    {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.15,
    },
  );

  fadeBlocks.forEach((block) => observer.observe(block));
}

// Vimeo API Initialisation
const initVimeoPlayers = () => {
  if (!window.Vimeo) return;

  const players = {};

  document
    .querySelectorAll("[data-vimeo-url], [data-vimeo-id]")
    .forEach((container) => {
      const id = container.id;
      if (!id) return;

      // Let Vimeo parse the data attributes and create the player natively
      // We use data-vimeo-url now to support unlisted hashes properly
      const player = new window.Vimeo.Player(container);
      players[id] = player;

      // Force native mute initially; reveal the poster after playback begins.
      player
        .ready()
        .then(() => {
          player.setVolume(0).catch(() => {});
        })
        .catch(() => {});

      player.on("play", () => {
        window.dispatchEvent(
          new CustomEvent("video-ready", { detail: { target: id } }),
        );
      });
    });

  // Listen to custom Alpine dispatches
  document.addEventListener("vimeo-action", (e) => {
    const targetId = e.detail?.target;
    const action = e.detail?.action;
    const player = players[targetId];

    if (player) {
      if (action === "unmute") {
        player.setVolume(1).catch(() => {});
      } else if (action === "mute") {
        player.setVolume(0).catch(() => {});
      } else if (action === "play") {
        player.play().catch(() => {});
      } else if (action === "pause") {
        player.pause().catch(() => {});
      }
    }
  });
};

document.addEventListener("DOMContentLoaded", initVimeoPlayers);
